import {
  select,
  take,
  takeLatest,
  takeLeading,
  takeEvery,
  call,
  race,
  delay,
  fork,
  spawn,
  join,
  cancel,
} from 'redux-saga/effects';
import { Rpc } from '@dynamika/rpc-core/contracts';
import { DICOMViewerSlice } from './DICOMViewer.slice';
import { dispatch, getUrlParams } from '../helpers';
import { IS_LOCALHOST } from '../../env';
import { StudyType } from '@dynamika-shared/services/communication';
import { viewerRpcScheme as viewerRpc } from 'src/services/rpc/viewer/scheme';
import { goToRoute, runToast } from '../tools';
import { prepareUrl } from 'src/utils/routerHelpers';
import { replacePathParams } from 'src/utils/replacePathWithParams';
import {
  isViewerAllowedToLoad,
  prepareStudyState,
} from './DICOMViewer.selectors';
import { CloseViewerAction, RunViewerAction } from './DICOMViewer.types';
import { fetchQCNeededDataWorker } from './modes/QC/QC.saga';
import { fetchReaderNeededDataWorker } from './modes/Reader/Reader.saga';
import { fetchSimpleNeededDataWorker } from '@restore/DICOMViewer/modes/SimpleViewer/SimpleViewer.saga';
import { Task } from 'redux-saga';
import { UserRoles } from '@dynamika-shared/types/auth';
import { selectAuthState } from '../auth/auth.slice';
import { OHIFViewerModeType } from '@dynamika-shared/types/viewerMode';
import { RootState } from '../store';
import { routesPath } from '@router/routes';
import { clinicalTrialContextSlice } from '../clinicalTrialContext/clinicalTrialContext.slice';
import { readerTaskSlice } from '../readerTask/readerTask.slice';
import { readerSubTaskSlice } from '../readerSubTask/readerSubTask.slice';
import { runDICOMDownloadAudit } from 'src/api/qcProcesses';
import { PayloadAction } from '@reduxjs/toolkit';
import { requestReattachWorker } from '@restore/readerViewerSession/tabCoordination/tabCoordination.saga';

// Same detached-route probe the form host uses (`InjectScoringFormComponent`).
// Kept as a `call`-able function so the guard in `runViewerWorker` stays
// assertable from a saga test.
export const isDetachedViewerRoute = (): boolean =>
  typeof location !== 'undefined' &&
  location.pathname.includes('viewer-detached');

// EXPERIMENT D7-7003 Option A: skip 5s handshake when iframe is already warm.
// Set to true once VIEWER__READY has been observed in this session; reset on
// closeViewerWorker (iframe is being torn down).
let viewerEverReady = false;
const HANDSHAKE_TIMEOUT_COLD_MS = 5000;
// A rebooting iframe announced itself ~700ms after the switch; leave headroom.
const STUDY_RESEND_WINDOW_MS = 10000;

const HANDSHAKE_TIMEOUT_WARM_MS = 200;

const {
  fetchNeededData,
  setViewerAllowedToLoad,
  loadingViewer,
  openViewer,
  closeViewer,
  checkViewer,
  runViewer,
} = DICOMViewerSlice.actions;


const DICOMTokenUrls = {
  imagingSpecialist: '/qc/tasks/:taskId/dicom/token',
  reader: '/reader/tasks/:taskId/dicom/token',
  viewer: '/viewer/tasks/:taskId/dicom/token',
  simple: '/viewer/is-tasks/:taskId/dicom/token',
};

const getDICOMTokenUrl = (role: UserRoles, mode: OHIFViewerModeType) => {
  let tokenUrl = '';

  // reader role + reader mode = reader token
  // sponsor role + reader mode = viewer token
  // admin role + reader mode = viewer token
  // pm role + reader mode = viewer token
  // imaging specialist role + imaging specialist mode = imaging specialist token
  // sponsor role + simple mode = simple token
  // admin role + simple mode = simple token
  // pm role + simple mode = simple token

  if (mode === OHIFViewerModeType.SIMPLE_VIEWER) {
    tokenUrl = DICOMTokenUrls.simple;
  } else if (mode === OHIFViewerModeType.READER) {
    tokenUrl =
      role === UserRoles.READER ? DICOMTokenUrls.reader : DICOMTokenUrls.viewer;
  } else if (
    mode === OHIFViewerModeType.IMAGING_SPECIALIST &&
    role === UserRoles.IMAGING_SPECIALIST
  ) {
    tokenUrl = DICOMTokenUrls.imagingSpecialist;
  }

  return tokenUrl;
};

export function* fetchNeededDataWorker(action: RunViewerAction) {
  switch (action.payload.mode) {
    case OHIFViewerModeType.IMAGING_SPECIALIST:
      yield call(fetchQCNeededDataWorker);
      break;
    case OHIFViewerModeType.READER:
      yield call(fetchReaderNeededDataWorker);
      break;
    case OHIFViewerModeType.SIMPLE_VIEWER:
      if (
        action.payload.role === UserRoles.PROJECT_MANAGER ||
        action.payload.role === UserRoles.SPONSOR
      ) {
        yield call(fetchSimpleNeededDataWorker);
      } else {
        yield call(fetchQCNeededDataWorker);
      }
      break;
    default:
      throw new Error('Unknown viewer mode');
  }
}

/**
 * Closes viewer and redirects to QC task info
 */
export function* closeViewerWorker(action: CloseViewerAction) {
  // D7-7003 warm-iframe: the iframe is being torn down, so the next viewer
  // load boots from scratch and must go through the full handshake again.
  viewerEverReady = false;

  const { id: clinicalTrialId, taskId, subTaskId } = yield call(getUrlParams);
  const authState = (yield select(selectAuthState)) as RootState['auth'];
  const role = authState.data?.selectedRole as UserRoles;
  const mode = action.payload;

  const urlsMap: Record<
    OHIFViewerModeType,
    Partial<Record<UserRoles, string>>
  > = {
    [OHIFViewerModeType.IMAGING_SPECIALIST]: {
      [UserRoles.IMAGING_SPECIALIST]: routesPath.qcTaskInfo,
    },
    [OHIFViewerModeType.READER]: {
      [UserRoles.READER]: routesPath.readerClinicalTrial,
      [UserRoles.SPONSOR]: routesPath.sponsorReadingsCompleted,
      [UserRoles.EXTERNAL_USER]: routesPath.externalUserSubjectsClinicalTrial,
      [UserRoles.PROJECT_MANAGER]:
        routesPath.clinicalTrialOverviewReadingsCompleted,
      [UserRoles.ADMIN]: routesPath.clinicalTrialOverviewReadingsCompleted,
    },
    [OHIFViewerModeType.SIMPLE_VIEWER]: {
      [UserRoles.SPONSOR]: routesPath.sponsorUploads,
      [UserRoles.PROJECT_MANAGER]: routesPath.clinicalTrialOverviewUploads,
      [UserRoles.ADMIN]: routesPath.clinicalTrialOverviewUploads,
    },
  };

  const url = urlsMap?.[mode]?.[role] || routesPath.qcTaskInfo;

  if (action.payload === OHIFViewerModeType.IMAGING_SPECIALIST) {
    yield dispatch(closeViewer());
  }

  if (clinicalTrialId && taskId) {
    yield goToRoute(prepareUrl(url, [clinicalTrialId, subTaskId || taskId]));
  }
}

/**
 *  Checks if viewer is allowed to load
 *  Fetches needed data
 *  Waits for fetchNeededDataSuccess
 *  Checks if viewer is allowed to load
 *  If not, closes viewer
 *  If allowed, sets viewerAllowedToLoad
 * @param action - action with payload that contains OHIFViewerModeEnum
 */
export function* checkViewerWorker(action: RunViewerAction) {
  yield call(fetchNeededDataWorker, fetchNeededData(action.payload));

  const getAllowedToLoad = isViewerAllowedToLoad(action.payload.mode);

  const _isViewerAllowedToLoad: ReturnType<typeof getAllowedToLoad> =
    yield select(getAllowedToLoad);

  if (
    !_isViewerAllowedToLoad &&
    action.payload.mode === OHIFViewerModeType.READER
  ) {
    yield runToast('error', 'No data to display');
  }

  yield dispatch(setViewerAllowedToLoad(!_isViewerAllowedToLoad));

  return _isViewerAllowedToLoad;
}

/**
 *  Runs viewer with provided mode
 *  Checks if viewer is allowed to load
 *  If not, closes viewer
 *  If allowed, loads study and opens viewer
 *  Waits for viewer to be ready
 *  Prepares study state
 *  Load study in viewer
 * @param action - action with payload that contains OHIFViewerModeEnum
 */
/**
 * Re-sends the study if the viewer announces itself after we already spoke.
 *
 * Plain terms
 * -----------
 * D7 gives the iframe a short head start and then tells it which study to open.
 * That is fine while the viewer is alive, but after the reader draws a
 * measurement the next switch reloads the iframe — the announcement lands in a
 * window that no longer exists and the reader gets a black viewer. So we listen:
 * if the viewer says "I am listening" after we already spoke, we say it again.
 *
 * Technical
 * ---------
 * `viewerEverReady` is only reset by `closeViewerWorker`, so a visit switch
 * always takes the 200ms warm handshake even when the iframe is rebooting
 * (~700ms observed). Rather than widen that timeout for everyone, this repeats
 * the announcement on the next START_LISTENING. Both messages are plain
 * notifications and `loadStudyWorker` is idempotent, so a duplicate is harmless;
 * for a viewer that never reloaded no second START_LISTENING arrives and this
 * task simply expires.
 */
function* resendStudyIfViewerReboots(payload: unknown, user: unknown) {
  const { listening } = yield race({
    listening: take(Rpc.Events.Viewer.START_LISTENING),
    timeout: delay(STUDY_RESEND_WINDOW_MS),
  });

  if (!listening) {
    return;
  }

  yield call(viewerRpc.events.loadStudy, payload);

  if (user) {
    yield call(viewerRpc.events.setUser, user);
  }
}

export function* runViewerWorker(action: RunViewerAction) {
  /**
   * The detached window must never drive a viewer
   *
   * Plain terms
   * -----------
   * When the reader pops the scoring form out into its own window, the images
   * stay in the main window — the popped-out one shows the form only. Without
   * this check the popped-out window still runs the whole "open the study"
   * routine, and its commands reach the main window: a couple of seconds after
   * the form is detached the main window reloads the study, drops back to a
   * single viewport and re-loads the series.
   *
   * Technical
   * ---------
   * `detectIsViewerByUrl` lists the `viewer-detached` routes as viewer pages, so
   * the task orchestration (`handleNormalTaskSaga`, `handleGroupTaskSaga`)
   * dispatches `runViewer` in the spawn tab too. This worker then emits
   * `D7__LOAD_STUDY` / `D7__SET_USER`, which the comms shim broadcasts on the
   * shared bus (`initRpc`: `peer.emit(type, payload)` with no `to`), and the
   * viewer answers `D7__LOAD_STUDY` with `hardLoadStudyWorker` — a full
   * re-initialisation (`study.saga.ts:249`). The spawn owns no OHIF iframe, so
   * the flow is a no-op for itself and a reload for the main tab.
   *
   * `openViewer()` still fires: `ReadingTaskViewerPageDetached` gates both its
   * spinner and the form render on `viewerState !== 'idle'`, so skipping it
   * strands the detached window on an endless loader. The form's own data comes
   * from detached initialization, not from this flow.
   */
  const isDetachedWindow: boolean = yield call(isDetachedViewerRoute);
  if (isDetachedWindow) {
    yield dispatch(openViewer());
    return;
  }

  const isViewerAllowedToLoad: boolean = yield call(checkViewerWorker, action);

  if (!isViewerAllowedToLoad) {
    yield call(closeViewerWorker, {
      type: action.type,
      payload: action.payload.mode,
    });
    return;
  }

  // Always reflect restart in UI to avoid stale 'opened' state
  yield dispatch(loadingViewer());

  if (IS_LOCALHOST) {
    try {
      yield call(fetch, '/viewer');
    } catch (e) {
      console.log('Viewer is not running');
      yield dispatch(openViewer());
      return;
    }
  }

  // D7-7003: shorten the "is the viewer listening yet?" wait when the viewer
  // iframe is already warm (still running from a previous visit).
  //   Cold (first viewer open): keep the full 5s — the iframe is still booting.
  //   Warm (later visit switches): 200ms is enough — it answers in <100ms.
  const handshakeTimeoutMs = viewerEverReady
    ? HANDSHAKE_TIMEOUT_WARM_MS
    : HANDSHAKE_TIMEOUT_COLD_MS;
  // Handshake: either START_LISTENING or READY could arrive first
  yield race({
    started: take(Rpc.Events.Viewer.START_LISTENING),
    readyEarly: take(Rpc.Events.Viewer.READY),
    timeout: delay(handshakeTimeoutMs),
  });

  const dataToSend: StudyType = yield select(
    prepareStudyState(action.payload.mode)
  );

  // Prepare a READY waiter before sending load command to not miss fast READY
  const waitReadyTask: Task<boolean> = yield fork(function* () {
    yield take(Rpc.Events.Viewer.READY);
  });

  const urlParams: { taskId: string; subTaskId?: string } =
    yield call(getUrlParams);
  const { taskId } = urlParams;
  const tokenTemplate = getDICOMTokenUrl(
    action.payload.role,
    action.payload.mode
  );
  /**
   * The DICOM token is scoped to the task that OWNS the images, not the one being scored
   *
   * Plain terms
   * -----------
   * A reading that spans several visits is one parent task owning one task per
   * visit. The images of every visit belong to the reading as a whole, so the
   * permission to view them has to be asked for at that level. Asked for at the
   * level of the visit being scored, the reader gets permission for that visit
   * only — and every earlier visit comes back refused, however correctly the
   * viewer lists it.
   *
   * Technical
   * ---------
   * The proxy issues a JWT whose `studies` allow-list is derived from the task
   * named in this URL: the parent answers with every child's study, a child with
   * its own. `taskId` here is already the parent — `getUrlParams` reads it from
   * the route, with the child in `subTaskId` — so dropping `subTaskId` from the
   * lookup is the entire fix, and it is a no-op for a non-grouped reading, where
   * `subTaskId` is absent and the parent id is the only id.
   *
   * This is also the ONLY place the scope is decided. `fetchDicomStoreToken` in
   * the viewer does `url.replace(':taskId', taskId)`, but the template is
   * already resolved by the time it is sent, so that replace never matches and
   * the id the viewer holds cannot change the request (D7-7712, C9).
   */
  const tokenUrl = replacePathParams(tokenTemplate, {
    taskId,
  }).replace(/\/+/g, '/');

  // Send LOAD_STUDY to viewer with mode
  const studyPayload = {
    ...dataToSend,
    viewerMode: action.payload.mode,
    tokenUrl,
  };

  yield call(viewerRpc.events.loadStudy, studyPayload);

  // Send user data to OHIF for all modes
  const authState = (yield select(selectAuthState)) as RootState['auth'];
  if (authState.data) {
    yield call(viewerRpc.events.setUser, authState.data);
  }

  yield spawn(resendStudyIfViewerReboots, studyPayload, authState.data);

  // D7-7003: on the warm path, don't wait for the viewer to re-confirm "ready"
  // after we send it the new study. The viewer iframe currently does NOT re-emit
  // VIEWER__READY after a warm load, so waiting here would just time out (~10s).
  // Opening the viewer panel is a no-op when it's already open (warm case), so we
  // open it right away and let the iframe catch up on its own.
  // TODO(RPC): once host↔viewer messaging moves to RPC (request/response), replace
  // this timeout-based skip with a real awaited "ready" reply instead of guessing.
  if (viewerEverReady) {
    // Cancel the leftover "wait for READY" task — otherwise it would wait on
    // VIEWER__READY forever, leaking one stale task per warm switch.
    yield cancel(waitReadyTask);
  } else {
    // Cold first load — keep the original 10s timeout so the viewer panel does
    // not open ahead of the iframe boot.
    yield race({
      done: join(waitReadyTask),
      timeout: delay(10000),
    });
  }

  // Open panel after READY (or warm-path immediate, or cold timeout fallback)
  yield dispatch(openViewer());
}

export function* attachFormWorker() {
  yield call(requestReattachWorker);
}

export function* DICOMAuditWorker(
  action: PayloadAction<{
    studies: {
      studyInstanceUid: string;
      series: { seriesInstanceUid: string }[];
    }[];
  }>
) {
  try {
    const context: ReturnType<
      typeof clinicalTrialContextSlice.selectors.selectConfig
    > = yield select(clinicalTrialContextSlice.selectors.selectConfig);
    const readerTaskState: ReturnType<
      typeof readerTaskSlice.selectors.selectState
    > = yield select(readerTaskSlice.selectors.selectState);
    const readerSubTaskState: ReturnType<
      typeof readerSubTaskSlice.selectors.selectState
    > = yield select(readerSubTaskSlice.selectors.selectState);

    const clinicalTrialId = context?.id;
    const taskId = readerTaskState?.data?.id;
    const subTaskId = readerSubTaskState?.currentSubTaskId;

    if (!clinicalTrialId || !taskId) {
      throw new Error('Missing clinicalTrialId or taskId');
    }
    yield call(
      runDICOMDownloadAudit,
      clinicalTrialId,
      taskId,
      {
        dicomEntities: action.payload.studies.map(study => ({
          studyInstanceUid: study.studyInstanceUid,
          seriesInstanceUids: study.series.map(s => s.seriesInstanceUid),
        })),
      },
      subTaskId
    );
  } catch (error) {
    console.error('Error running DICOM audit:', error);
  }
}

// D7-7003 warm-iframe: the first VIEWER__READY proves the viewer iframe has
// fully booted (mode entered, services up, handshake complete). From that
// point the iframe is "warm" — subsequent task loads reuse it, so the
// 5s+10s handshake race waits can be skipped (see runViewerWorker).
function markViewerReadyWorker() {
  viewerEverReady = true;
}

export function* DICOMViewerSaga() {
  yield takeEvery(
    Rpc.Events.Viewer.READY,
    markViewerReadyWorker
  );
  yield takeLatest(runViewer.type, runViewerWorker);
  yield takeLatest(fetchNeededData.type, fetchNeededDataWorker);
  yield takeLatest(checkViewer.type, checkViewerWorker);
  yield takeLatest(
    Rpc.Events.Viewer.CLOSED,
    closeViewerWorker
  );
  // A repeated click must not cancel snapshot restoration already in progress.
  yield takeLeading(
    Rpc.Events.Viewer.ATTACH_FORM,
    attachFormWorker
  );

  yield takeLatest(DICOMViewerSlice.actions.downloadDICOMAudit.type, DICOMAuditWorker);
}
